import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Terminal, ArrowRight, Loader2, ArrowLeft, Lock } from "lucide-react";
import { auth, formatApiErrorDetail } from "@/lib/api";
import BrandMark from "@/components/BrandMark";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (auth.isAuthenticated()) {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await auth.login(email.trim().toLowerCase(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#F7F6F1] text-zinc-900"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 30%, rgba(22,163,74,0.10), transparent 45%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.45]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,10,10,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,10,0.06) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(ellipse at center, black 35%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 35%, transparent 80%)",
        }}
      />
      <div className="grain-overlay" />

      <header className="relative z-10 flex items-center justify-between border-b border-zinc-200 px-6 py-4 sm:px-10 lg:px-16">
        <div className="flex items-center gap-3 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-500">
          <Terminal className="h-3.5 w-3.5 text-emerald-600" />
          <BrandMark size="inline" /> / admin
        </div>
        <Link
          to="/"
          data-testid="back-home-link"
          className="group inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-600 transition-colors hover:text-emerald-700"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          Back home
        </Link>
      </header>

      <section className="relative z-10 mx-auto flex w-full max-w-md flex-col px-6 pb-20 pt-20 sm:pt-28">
        <div className="reveal">
          <div className="mb-6 inline-flex w-fit items-center gap-2 border border-zinc-300 bg-white/70 px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-600 backdrop-blur">
            <Lock className="h-3 w-3 text-emerald-600" />
            secure access
          </div>
          <h1
            data-testid="admin-login-title"
            className="font-display text-5xl font-extrabold leading-[0.95] tracking-tight text-zinc-900 sm:text-6xl"
          >
            Admin
            <br />
            <span className="text-emerald-700">console.</span>
          </h1>
          <p className="mt-4 font-mono-tech text-[11px] uppercase tracking-[0.3em] text-zinc-500">
            // waitlist · b2b inquiries · csv export
          </p>

          <form
            onSubmit={onSubmit}
            data-testid="admin-login-form"
            className="mt-10 flex flex-col gap-5 border border-zinc-300 bg-white p-6 shadow-[0_1px_0_0_rgba(10,10,10,0.04),0_24px_60px_-30px_rgba(10,10,10,0.18)] sm:p-7"
          >
            <Field
              label="01 — Email"
              testId="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@deployd.app"
              autoFocus
            />
            <Field
              label="02 — Password"
              testId="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />

            {error && (
              <p
                data-testid="admin-login-error"
                className="border border-red-300 bg-red-50 px-3 py-2 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-red-700"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting || !email || !password}
              className="group mt-1 inline-flex items-center justify-between border border-zinc-900 bg-zinc-900 px-5 py-3 font-mono-tech text-[11px] uppercase tracking-[0.28em] text-white transition-colors hover:border-emerald-600 hover:bg-emerald-600 disabled:opacity-60"
            >
              <span>{submitting ? "Authenticating..." : "Sign in"}</span>
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              )}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

const Field = ({ label, testId, ...rest }) => (
  <div className="flex flex-col space-y-2">
    <label className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500">
      {label}
    </label>
    <input
      data-testid={testId}
      required
      {...rest}
      className="w-full border border-zinc-300 bg-white px-4 py-3 font-mono-tech text-sm text-zinc-900 placeholder-zinc-400 outline-none transition-colors focus:border-emerald-500"
    />
  </div>
);
